import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import api from '../../services/api';
import { confirmAction, confirmDelete } from '../../utils/confirm';

function CredentialsPopup({ credentials, emailSent, onClose }) {
  const copy = (text) => { navigator.clipboard.writeText(text); toast.success('Copied'); };
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <h2>Login Credentials</h2>
        <div className="credentials-box">
          {emailSent ? (
            <div className="credentials-email-sent">Credentials emailed to the user.</div>
          ) : (
            <div className="credentials-email-failed">Email not sent. Share credentials manually.</div>
          )}
          <div className="credentials-row">
            <label>Username</label>
            <div className="credentials-value">
              <code>{credentials.username}</code>
              <button className="btn btn-sm btn-outline" onClick={() => copy(credentials.username)}>Copy</button>
            </div>
          </div>
          <div className="credentials-row">
            <label>One-Time Password</label>
            <div className="credentials-value">
              <code className="otp-code">{credentials.oneTimePassword}</code>
              <button className="btn btn-sm btn-outline" onClick={() => copy(credentials.oneTimePassword)}>Copy</button>
            </div>
          </div>
          <div className="credentials-warning">This password will not be shown again.</div>
        </div>
        <div className="form-actions">
          <button className="btn btn-primary" onClick={onClose}>Done</button>
        </div>
      </div>
    </div>
  );
}

function UserModal({ tenantId, user, availableRoles, onClose, onSaved }) {
  const isEdit = !!user;
  const [form, setForm] = useState({
    fullName: user?.FullName || '',
    username: user?.Username || '',
    email: user?.Email || '',
    mobileNo: user?.MobileNo || '',
    department: user?.Department || '',
    roleIds: user?.assignedRoles?.map((r) => r.RoleID) || [],
    password: '',
    sendEmail: true,
  });
  const [saving, setSaving] = useState(false);

  const handleChange = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const toggleRole = (roleId) => {
    setForm((prev) => ({
      ...prev,
      roleIds: prev.roleIds.includes(roleId)
        ? prev.roleIds.filter((id) => id !== roleId)
        : [...prev.roleIds, roleId],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.fullName.trim() || !form.email.trim()) {
      toast.error('Name and Email are required');
      return;
    }
    if (!isEdit && !form.username.trim()) {
      toast.error('Username is required');
      return;
    }

    setSaving(true);
    try {
      if (isEdit) {
        await api.put(`/tenant-users/${user.UserID}`, {
          fullName: form.fullName,
          email: form.email,
          mobileNo: form.mobileNo,
          department: form.department,
          roleIds: form.roleIds,
        });
        toast.success('User updated');
        onSaved(null);
      } else {
        const response = await api.post('/tenant-users', {
          tenantId,
          fullName: form.fullName,
          username: form.username,
          email: form.email,
          mobileNo: form.mobileNo,
          department: form.department,
          roleIds: form.roleIds,
          password: form.password || undefined,
          sendEmail: form.sendEmail,
        });
        if (response.data.success) {
          toast.success(response.data.message);
          onSaved(response.data.credentials, response.data.emailSent);
        }
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-lg" onClick={(e) => e.stopPropagation()}>
        <h2>{isEdit ? 'Edit User' : 'Add New User'}</h2>
        <form onSubmit={handleSubmit}>
          <div className="form-row">
            <div className="form-group">
              <label className="required-label">Full Name</label>
              <input type="text" placeholder="Full Name" value={form.fullName} onChange={(e) => handleChange('fullName', e.target.value)} autoFocus />
            </div>
            <div className="form-group">
              <label className="required-label">Email</label>
              <input type="email" placeholder="Email" value={form.email} onChange={(e) => handleChange('email', e.target.value)} />
            </div>
          </div>
          <div className="form-row">
            {!isEdit && (
              <div className="form-group">
                <label className="required-label">Username</label>
                <input type="text" placeholder="Username" value={form.username} onChange={(e) => handleChange('username', e.target.value.toLowerCase().replace(/\s/g, ''))} />
              </div>
            )}
            <div className="form-group">
              <label>Mobile Number</label>
              <input type="text" placeholder="Mobile Number" value={form.mobileNo} onChange={(e) => handleChange('mobileNo', e.target.value)} />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Department</label>
              <input type="text" placeholder="Department" value={form.department} onChange={(e) => handleChange('department', e.target.value)} />
            </div>
            {!isEdit && (
              <div className="form-group">
                <label>Password (Optional)</label>
                <input type="password" placeholder="Leave blank to auto-generate" value={form.password} onChange={(e) => handleChange('password', e.target.value)} />
                <span className="form-hint">Leave blank to auto-generate</span>
              </div>
            )}
          </div>

          {/* Role Assignment */}
          <div className="form-group">
            <label className="required-label">Roles</label>
            <div className="role-chips-container">
              {availableRoles.length === 0 ? (
                <span style={{ color: '#999', fontSize: 13 }}>No roles created yet. Go to Roles tab to create roles first.</span>
              ) : (
                availableRoles.map((r) => (
                  <label key={r.RoleID} className={`role-chip ${form.roleIds.includes(r.RoleID) ? 'selected' : ''}`}>
                    <input type="checkbox" checked={form.roleIds.includes(r.RoleID)} onChange={() => toggleRole(r.RoleID)} />
                    <span className="role-chip-name">{r.RoleName}</span>
                    {r.SegmentName && <span className="role-chip-segment">{r.SegmentName}</span>}
                  </label>
                ))
              )}
            </div>
          </div>

          {!isEdit && (
            <div className="form-group">
              <label className="checkbox-inline">
                <input type="checkbox" checked={form.sendEmail} onChange={(e) => handleChange('sendEmail', e.target.checked)} />
                Send welcome email with login credentials
              </label>
            </div>
          )}

          <div className="form-actions">
            <button type="button" className="btn btn-cancel-text" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-save" disabled={saving}>
              {saving ? 'Saving...' : isEdit ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function UsersTab({ tenantId }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [availableRoles, setAvailableRoles] = useState([]);
  const [showCreds, setShowCreds] = useState(null);
  const [credsEmailSent, setCredsEmailSent] = useState(false);

  // Filters
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [roleFilter, setRoleFilter] = useState('');

  const loadUsers = useCallback(async () => {
    if (!tenantId) return;
    try {
      setLoading(true);
      const params = new URLSearchParams({ tenantId });
      if (search) params.append('search', search);
      if (statusFilter) params.append('status', statusFilter);
      if (roleFilter) params.append('roleId', roleFilter);
      const response = await api.get(`/tenant-users?${params}`);
      if (response.data.success) setUsers(response.data.data);
    } catch {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [tenantId, search, statusFilter, roleFilter]);

  const loadRoles = useCallback(async () => {
    if (!tenantId) return;
    try {
      const response = await api.get(`/tenant-users/roles-list/${tenantId}`);
      if (response.data.success) setAvailableRoles(response.data.data);
    } catch { /* silent */ }
  }, [tenantId]);

  useEffect(() => { loadUsers(); }, [loadUsers]);
  useEffect(() => { loadRoles(); }, [loadRoles]);

  const handleSaved = (credentials, emailSent) => {
    setShowModal(false);
    setEditUser(null);
    loadUsers();
    if (credentials) {
      setShowCreds(credentials);
      setCredsEmailSent(!!emailSent);
    }
  };

  const handleResetPassword = async (userId) => {
    if (!(await confirmAction('Reset Password?', 'This will generate a new one-time password and email it to the user.', { confirmText: 'Yes, reset', confirmColor: '#e65100', icon: 'question' }))) return;
    try {
      const response = await api.post(`/tenant-users/${userId}/reset-password`);
      if (response.data.success) {
        toast.success(response.data.message);
        setShowCreds(response.data.credentials);
        setCredsEmailSent(!!response.data.emailSent);
        loadUsers();
      }
    } catch {
      toast.error('Failed to reset password');
    }
  };

  const handleDelete = async (userId) => {
    if (!(await confirmDelete('User'))) return;
    try {
      await api.delete(`/tenant-users/${userId}`);
      toast.success('User deleted');
      loadUsers();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete');
    }
  };

  const handleToggleActive = async (userId, currentActive) => {
    try {
      await api.put(`/tenant-users/${userId}`, { isActive: !currentActive });
      toast.success(currentActive ? 'User deactivated' : 'User activated');
      loadUsers();
    } catch {
      toast.error('Failed to update status');
    }
  };

  const getInitials = (name) => name ? name.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2) : '??';
  const getLoginStatus = (u) => {
    if (u.MustChangePassword) return { label: 'Pending', cls: 'badge-warning' };
    if (u.LastLoginAt) return { label: 'Logged In', cls: 'badge-active' };
    return { label: 'Never', cls: 'badge-inactive' };
  };

  return (
    <>
      {/* Filters */}
      <div className="user-filters">
        <div className="search-box">
          <span className="search-icon">🔍</span>
          <input type="text" placeholder="Search users..." value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="filter-select">
          <option value="">Status</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
        <select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)} className="filter-select">
          <option value="">Role</option>
          {availableRoles.map((r) => (
            <option key={r.RoleID} value={r.RoleID}>{r.RoleName}</option>
          ))}
        </select>
        <div style={{ flex: 1 }} />
        <button className="btn btn-add" onClick={() => { setEditUser(null); setShowModal(true); }}>
          + Add User
        </button>
      </div>

      {/* Users Table */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>Loading...</div>
      ) : users.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>No users found.</div>
      ) : (
        <table className="data-table" style={{ marginTop: 16 }}>
          <thead>
            <tr>
              <th>User</th>
              <th>Email</th>
              <th>Mobile No</th>
              <th>Role</th>
              <th>Department</th>
              <th>Login Status</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => {
              const loginSt = getLoginStatus(u);
              return (
                <tr key={u.UserID}>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div className="table-avatar">{getInitials(u.FullName)}</div>
                      <div>
                        <div style={{ fontWeight: 600 }}>{u.FullName}</div>
                        <div style={{ fontSize: 12, color: '#999' }}>{u.Username}</div>
                      </div>
                    </div>
                  </td>
                  <td>{u.Email}</td>
                  <td>{u.MobileNo || '-'}</td>
                  <td>
                    <div className="role-badges">
                      {u.Role === 'InstituteAdmin' && <span className="badge badge-provider">Institute Admin</span>}
                      {u.assignedRoles.map((r) => (
                        <span key={r.RoleID} className="badge badge-role">{r.RoleName}</span>
                      ))}
                      {u.Role !== 'InstituteAdmin' && u.assignedRoles.length === 0 && '-'}
                    </div>
                  </td>
                  <td>{u.Department || '-'}</td>
                  <td><span className={`badge ${loginSt.cls}`}>{loginSt.label}</span></td>
                  <td>
                    <span className={`badge ${u.IsActive ? 'badge-active' : 'badge-inactive'}`}>
                      {u.IsActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button className="action-btn" title={u.IsActive ? 'Deactivate' : 'Activate'} onClick={() => handleToggleActive(u.UserID, u.IsActive)} style={{ color: u.IsActive ? '#e65100' : '#2e7d32' }}>
                        {u.IsActive ? '⊘' : '✓'}
                      </button>
                      <button className="action-btn action-edit" title="Edit" onClick={() => { setEditUser(u); setShowModal(true); }}>✏</button>
                      <button className="action-btn action-reset" title="Reset Password" onClick={() => handleResetPassword(u.UserID)}>🔑</button>
                      {u.Role !== 'InstituteAdmin' && (
                        <button className="action-btn action-delete" title="Delete" onClick={() => handleDelete(u.UserID)}>🗑</button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {showModal && (
        <UserModal
          tenantId={tenantId}
          user={editUser}
          availableRoles={availableRoles}
          onClose={() => { setShowModal(false); setEditUser(null); }}
          onSaved={handleSaved}
        />
      )}

      {showCreds && (
        <CredentialsPopup
          credentials={showCreds}
          emailSent={credsEmailSent}
          onClose={() => { setShowCreds(null); setCredsEmailSent(false); }}
        />
      )}
    </>
  );
}

export default UsersTab;
